<template>
    <Overlay :show="showOverlay">
        <b-row class="pb-2" v-if="showHeading">
            <b-col>
                <h4 v-if="null === id">
                    {{ $t('Page(OrganizationCard):Component(Text):add-new-organization', {type: type}) }}
                </h4>
                <h4 v-else>
                    {{ $t('Page(OrganizationCard):Component(Text):edit-organization-label', {organization: organization.title}) }}
                </h4>
            </b-col>
            <b-col md="6" lg="6">
                <Actions
                    class="float-right"
                    @save="save"
                    @close="close"
                />
            </b-col>
        </b-row>
        <b-tabs>
            <b-tab :title="$t('Page(OrganizationCard):Component(Tab):general')">
                <b-row>
                    <b-col md="6" lg="6">
                        <GeneralInfo
                            :show-domain="true"
                            :type="type"
                            :organization="organization"
                            :is-parent-visible="isParentSelectorVisible"
                            :show-valid-from-to-dates="true"
                            :is-location-visible="true"
                            @input="onGeneralInfoChange"
                        />
                    </b-col>
                    <b-col md="6" lg="6">
                        <Contacts
                            :selected="organization.contacts"
                            @save="onContactSave"
                            @remove="onRemoveContact"
                        />
                    </b-col>
                </b-row>
                <b-row v-if="isProductCategoryComponentVisible">
                    <b-col>
                        <ProductCategorySelect
                            :title="$t('Component(Text):taric-codes')"
                            @change="onProductCategoryChange"
                            :selected-items="organization.categories"
                        />
                    </b-col>
                </b-row>
            </b-tab>
            <b-tab :title="$t('Page(OrganizationCard):Component(Tab):echa-settings')">
                <b-row>
                    <b-col>
                        <S2SClientSettings ref="s2sClientSettings" v-if="id" :organization-id="id" />
                    </b-col>
                    <b-col v-if="$can('manage', 'organization-echa-sandbox-settings')">
                        <EchaSandBoxSettings
                            @input="onEchaSandBoxSettingsChange"
                            :settings="organization.settings.echaSettings ? organization.settings.echaSettings.sandBoxSettings : null"
                        />
                    </b-col>
                </b-row>
            </b-tab>
            <b-tab :title="$t('Page(OrganizationCard):Component(Tab):request-settings')">
                <b-row>
                    <b-col>
                        <b-card no-body class="p-2">
                            <b-form-group
                                :label="$t('Component(Placeholder):days-after-email-can-be-send')"
                                label-for="daysAfterEmailCanBeSend"
                            >
                                <b-form-input
                                    id="daysAfterEmailCanBeSend"
                                    type="number"
                                    :state="!organization.settings.requestSettings.daysAfterEmailCanBeSend ? false : null"
                                    v-model="organization.settings.requestSettings.daysAfterEmailCanBeSend"
                                    :placeholder="$t('Component(Placeholder):days-after-email-can-be-send')"
                                />
                            </b-form-group>

                            <b-form-group
                                :label="$t('Component(Placeholder):days-to-remind')"
                                label-for="daysToRemind"
                            >
                                <b-form-input
                                    id="daysToRemind"
                                    type="number"
                                    :state="!organization.settings.requestSettings.daysToRemind ? false : null"
                                    v-model="organization.settings.requestSettings.daysToRemind"
                                    :placeholder="$t('Component(Placeholder):days-to-remind')"
                                />
                            </b-form-group>

                            <b-form-group
                                :label="$t('Component(Placeholder):reply-process-flow')"
                                label-for="replyProcessFlow"
                            >
                                <v-select
                                    id="replyProcessFlow"
                                    v-model="replyProcessFlow"
                                    class="bg-white mb-1"
                                    label="title"
                                    :placeholder="$t('Component(Placeholder):reply-process-flow')"
                                    :options="replyProcessFlows"
                                    @input="onReplyProcessFlowChange"
                                />
                            </b-form-group>

                            <b-form-checkbox
                                v-model="organization.settings.requestSettings.showSendEmailStep"
                                switch
                                inline
                            >
                                {{ $t('Component(Placeholder):show-send-email-step') }}
                            </b-form-checkbox>

                            <b-form-checkbox
                                class="mt-1"
                                v-model="organization.settings.requestSettings.useCustomEmailTemplate"
                                switch
                                inline
                            >
                                {{ $t('Component(Placeholder):use-custom-email-template') }}
                            </b-form-checkbox>

                            <div
                                class="pt-1"
                                v-if="organization.settings.requestSettings.useCustomEmailTemplate"
                            >
                                <b-tabs>
                                    <b-tab
                                        v-for="language in languages"
                                        :title="language.code.toUpperCase()"
                                    >
                                        <b-form-group
                                            :label="$t('Page(SendRequest):Component(Label):email-subject')"
                                            :label-for="'subject' + language.code"
                                        >
                                            <b-form-input
                                                :id="'subject' + language.code"
                                                v-model="organization.sendRequestEmailTemplates[language.code].subject"
                                            />
                                        </b-form-group>

                                        <b-form-group
                                            :label="$t('Page(SendRequest):Component(Label):email-message')"
                                            :label-for="'content' + language.code"
                                        >
                                            <quill-editor
                                                :id="'content' + language.code"
                                                :options="editorOptions"
                                                v-model="organization.sendRequestEmailTemplates[language.code].body"
                                            />
                                        </b-form-group>
                                    </b-tab>
                                </b-tabs>
                            </div>
                        </b-card>
                    </b-col>
                </b-row>
            </b-tab>
            <b-tab :title="$t('Page(OrganizationCard):Component(Tab):product-statements')">
                <b-row>
                    <b-col>
                        <RegulatedProductStatementDeclaration
                            :properties="organization.regulatedGeneralProductStatements"
                        />
                    </b-col>
                </b-row>
            </b-tab>
            <b-tab
                v-if="organization.organizationLegislations"
                :title="$t('Page(OrganizationCard):Component(Tab):legislation-settings')"
            >
                <b-row>
                    <b-col>
                        <LegislationSettings
                            :organization-legislations="organization.organizationLegislations"
                        />
                    </b-col>
                </b-row>
            </b-tab>
        </b-tabs>
    </Overlay>
</template>

<script>
import {BRow, BCol, BTabs, BTab, BFormGroup, BFormInput, BCard, BFormCheckbox} from 'bootstrap-vue'
import {Actions, GeneralInfo} from "@/views/administration/organization/components";
import {ProductCategorySelect} from "@core/components/product";
import Contacts from "@/views/administration/organization/components/Contacts";
import {i18nApi, ProductApi, SupplyChain} from "@core/api";
import vSelect from 'vue-select'
import {OrganizationType} from "@/libs/supply-chain";
import S2SClientSettings from "@core/components/echa/S2SClientSettings.vue";
import EchaSandBoxSettings from "@/views/administration/organization/internal/components/EchaSandBoxSettings.vue";
import backendResponse from "@core/mixins/ui/backend-response";
import RegulatedProductStatementDeclaration from "@core/components/product/RegulatedProductStatementDeclaration.vue";
import Overlay from "@core/components/common/Overlay.vue";
import LegislationSettings from "@/views/administration/organization/internal/components/LegislationSettings.vue";
import { quillEditor } from 'vue-quill-editor'

export default {
    mixins: [backendResponse],
    components: {
        LegislationSettings, quillEditor, Overlay, RegulatedProductStatementDeclaration,
        BRow, BCol, BTabs, BTab, BFormGroup, BFormInput, BCard, BFormCheckbox, vSelect,
        Actions, S2SClientSettings, EchaSandBoxSettings, GeneralInfo, ProductCategorySelect, Contacts,
    },

    props: {
        type: {
            required: true
        },
        showHeading: {
            default: true
        },
        isRoot: {
            default: false
        },
        id: {
            default: null
        }
    },

    data() {
        return {
            editorOptions: {
                modules: {
                    toolbar: [
                        ['bold', 'italic', 'underline', 'strike'],
                        ['blockquote', 'code-block'],
                        [{ 'size': ['small', false, 'large', 'huge'] }]
                        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                        [{ 'indent': '-1'}, { 'indent': '+1' }],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'list': 'check' }],
                        ['link', 'image'],

                        [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
                        [{ 'font': [] }],
                        [{ 'align': [] }],

                        ['clean']
                    ]
                }
            },
            showOverlay: true,

            languages: [],
            defaultSendRequestEmailTemplates: null,
            productStatements: [],
            organizations: [],
            organization: {
                title: null,
                state: null,
                businessRoles: [],
                contacts: [],
                categories: [],
                legalIdentity: null,
                parent: null,
                validFrom: new Date(),
                validTo: new Date('2099-01-01'),
                location: {
                    address: null,
                    city: null,
                    zip: null,
                    coordinates: {
                        latitude: null,
                        longitude: null
                    },
                    unlocode: null
                },
                settings: {
                    echaSettings: {
                        sandBoxSettings: {
                            enabled: false,
                            namespace: ''
                        }
                    },
                    requestSettings: {
                        daysAfterEmailCanBeSend: 8,
                        daysToRemind: 5,
                        showSendEmailStep: false,
                        useCustomEmailTemplate: false,
                        replyProcessFlow: this.$store.getters.defaultReplyProcessFlow
                    }
                },
                regulatedGeneralProductStatements: [],
                sendRequestEmailTemplates: {},
                showProductStatistics: false
            },
            categories: [],
            contactIdIncrement: 1,
            replyProcessFlow: null
        }
    },

    methods: {
        onGeneralInfoChange(info) {
            this.organization.title = info.title;
            this.organization.legalIdentity = info.legalIdentity;
            this.organization.domain = info.domain;
            this.organization.state = info.state;
            this.organization.businessRoles = info.businessRoles;
            this.organization.parent = info.parent;
            this.organization.validFrom = info.validFrom;
            this.organization.validTo = info.validTo;
            this.organization.location = info.location;
            this.organization.showProductStatistics = info.showProductStatistics;
        },
        onReplyProcessFlowChange(rpf) {
            this.organization.settings.requestSettings.replyProcessFlow = rpf.id;
        },
        onProductCategoryChange(e) {
            this.categories = e.idList;
        },
        onRemoveContact(contact) {
            let index = this.organization.contacts.findIndex(c => c.id === contact.id);

            if (index >= 0) {
                this.organization.contacts.splice(index, 1);
            }
        },
        onContactSave(id, contact) {
            if (!id) {
                contact.id = (this.contactIdIncrement++) + 'id';
                contact.notPersisted = true;

                this.organization.contacts.push(contact);
            } else {
                let index = this.organization.contacts.findIndex(o => o.id === id);

                if (index !== -1) {
                    let organizationContact = this.organization.contacts[index];
                    organizationContact.name = contact.name;
                    organizationContact.position = contact.position;
                    organizationContact.description = contact.description;
                    organizationContact.communicationChannels = contact.communicationChannels;
                }
            }
        },
        gatherSaveAttributes() {
            let organization = {
                ...this.organization,
                state: this.organization.state ? this.organization.state.id : null,
                contacts: this.organization.contacts.map(c => {
                    if (c.notPersisted) {
                        let contact = {...c};
                        delete contact['id'];
                        delete contact['notPersisted'];
                        return contact;
                    }
                    return c;
                }),
            };

            organization.settings.requestSettings.daysAfterEmailCanBeSend = parseInt(organization.settings.requestSettings.daysAfterEmailCanBeSend);
            organization.settings.requestSettings.daysToRemind = parseInt(organization.settings.requestSettings.daysToRemind);

            if (organization.legalIdentity.type) {
                organization.legalIdentity = {
                    type: organization.legalIdentity.type.id,
                    value: organization.legalIdentity.value
                };
            }

            if (this.type === OrganizationType.INTERNAL) {
                organization.parent = this.organization.parent ? this.organization.parent.id : null;
                organization.businessRoles = this.organization.businessRoles.map(r => r.id);
                organization.categories = this.categories;
            }

            //  organization legislations is available only for root organization
            if (null === organization.parent) {
                organization.organizationLegislations = organization.organizationLegislations.map(ol => {
                    return {
                        isActive: ol.isActive,
                        legislation: ol.legislation.id
                    }
                });
            } else {
                delete organization.organizationLegislations;
            }

            return organization;
        },
        onEchaSandBoxSettingsChange(settings) {
            if (!this.organization.settings.hasOwnProperty('echaSettings') || null === this.organization.settings.echaSettings) {
                this.organization.settings.echaSettings = {sandBoxSettings: {}};
            }
            this.organization.settings.echaSettings.sandBoxSettings.enabled = settings.enabled;
            this.organization.settings.echaSettings.sandBoxSettings.namespace = settings.namespace;
        },
        save() {
            this.showOverlay = true;
            let promise = this.id ? this.$refs.s2sClientSettings.save() : new Promise((resolve) => resolve());

            promise
                .then(() => {
                    return this.saveInternalOrganization(this.gatherSaveAttributes());
                })
                .then((r) => {
                    this.$store.dispatch(
                        'updateShowSendEmailSettingsStepForOrganization',
                        this.organization.settings.requestSettings.showSendEmailStep
                    );

                    this.$store.dispatch(
                        'updateReplyProcessFlow',
                        this.organization.settings.requestSettings.replyProcessFlow
                    );

                    this.$store.dispatch(
                        'updateShowProductStatistics',
                        this.organization.showProductStatistics
                    );

                    if (this.id) {
                        //  when it is root
                        if (this.organization.parent === null) {
                            this.$store.dispatch('rootOrganizationUpdated');
                        }
                        return this.fetch();
                    }

                    if (r.data.id) {
                        this.onCreate(r.data.id);
                    }
                })
                .catch(this.handleBackendError)
                .finally(() => {
                    this.showOverlay = false;
                })
        },
        onCreate(id) {
            this.$router.push({
                name: 'administration-internal-edit',
                params: {type: this.type, id: id}
            });
        },
        close() {
            this.$router.go(-1);
        },
        fetchDefaultSendRequestEmailTemplates() {
            if (Array.isArray(this.organization.sendRequestEmailTemplates)) {
                ProductApi.getSendRequestEmailTemplates().then(r => {
                    this.organization.sendRequestEmailTemplates = r.data;
                });
            }
        },
        initReplyProcessFlow() {
            this.replyProcessFlow = this.replyProcessFlows
                .find(rp => rp.id === this.organization.settings.requestSettings.replyProcessFlow);
        },
        fetch() {
            return SupplyChain
                .getOrganizationCard(this.id)
                .then(r => {
                    this.organization = r.data;

                    this.fetchDefaultSendRequestEmailTemplates();
                    this.initReplyProcessFlow();

                    return ProductApi.getAllGeneralProductStatements().then((res) => {
                        const regulatedPropertiesMap = {};
                        this.organization.regulatedGeneralProductStatements.forEach(rs => {
                            regulatedPropertiesMap[rs.statement.id] = rs.coveredExcluded;
                        });
                        this.organization.regulatedGeneralProductStatements = res.data.map(statement => {
                            return {
                                statement: {
                                    id: statement.id,
                                    title: statement.organizationStatement
                                },
                                coveredExcluded: regulatedPropertiesMap.hasOwnProperty(statement.id) ? regulatedPropertiesMap[statement.id] : false
                            };
                        });
                    });
                });
        },
        saveInternalOrganization(organization) {
            let promise = this.id ? SupplyChain.updateInternalOrganization(organization) : SupplyChain.newInternalOrganization(organization);
            return promise.then(r => {
                this.showDefaultSuccessMessage(
                    this.id ? 'Organization has been successfully updated' : 'Organization has been successfully created'
                );
            });
        },
    },

    computed: {
        isProductCategoryComponentVisible() {
            return this.type === OrganizationType.INTERNAL;
        },
        isParentSelectorVisible() {
            if (this.type === OrganizationType.INTERNAL) {
                if (!this.organization.id) {
                    return true;
                }
                if (this.organization.parent) {
                    return true;
                }
                return this.organization.parent === null && !this.organization.id;
            }

            return false;
        },
        replyProcessFlows() {
            return this.$store.getters.replyProcessFlowsOptions;
        }
    },

    created() {
        if (this.id) {
            this.fetch().then(() => this.showOverlay = false);
        } else {
            setTimeout(() => this.showOverlay = false, 500);
        }
        i18nApi.getLanguages().then(r => {
            r.data.forEach(language => {
                if (this.organization.sendRequestEmailTemplates[language.code] === undefined) {
                    this.$set(this.organization.sendRequestEmailTemplates, language.code, '');
                }
            });
            this.languages = r.data
        });
    }
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/quill.scss';
</style>
